import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { useIsomorphicEffect } from '@/hooks/useIsomorphicEffect/useIsomorphicEffect';
import { cx } from '@/utils/cx';
import { createPortal } from 'react-dom';

import { usePopOverContext } from '../PopOverContext';

import styles from './PopOverContent.module.scss';

export type PopOverPlacement = 'bottom-start' | 'bottom-end';

export interface PopOverContentProps {
  children: ReactNode;
  /** 트리거 기준 위치 (기본: 'bottom-start') */
  placement?: PopOverPlacement;
  /** 트리거와의 간격 px (기본: 8) */
  offset?: number;
  /** viewport 경계에서의 최소 여백 px (기본: 0) */
  collisionPadding?: number;
  /**
   * portal content 의 z-index. 미지정 시 default (50).
   * 명시 시 content inline style 로 `--core-ui-popover-z-index` 변수에 inject — className 의 CSS 변수보다 우선.
   *
   * 호스트 layer 시스템에서 다른 overlay 와의 stacking 조정이 필요할 때 사용.
   */
  zIndex?: number;
  className?: string;
  /** 위치 오버라이드용 inline style */
  style?: CSSProperties;
}

export const PopOverContentImpl = ({
  children,
  placement = 'bottom-start',
  offset = 8,
  collisionPadding = 0,
  zIndex,
  className,
  style,
}: PopOverContentProps) => {
  const { open, contentId, contentRef, triggerRef, anchorEl } = usePopOverContext();
  const [position, setPosition] = useState<CSSProperties>({});

  // collisionPadding: Trigger 유무와 무관하게 항상 적용
  const collisionStyle: CSSProperties =
    collisionPadding > 0 ? { maxWidth: `calc(100vw - ${collisionPadding * 2}px)` } : {};
  // zIndex: 명시 시 CSS 변수로 inject — SCSS 의 `var(--core-ui-popover-z-index, 50)` 와 호환
  const zIndexStyle: CSSProperties | undefined =
    zIndex !== undefined ? ({ '--core-ui-popover-z-index': String(zIndex) } as CSSProperties) : undefined;
  const [mounted, setMounted] = useState(false);

  // SSR 안전: client에서만 portal — layout 효과로 document 리스너(부모 useEffect)보다 ref가 먼저 연결되게 함
  useIsomorphicEffect(() => {
    setMounted(true);
  }, []);

  // 위치 계산 — anchorEl 우선, triggerRef fallback
  useEffect(() => {
    const positioningEl = anchorEl ?? triggerRef.current;
    if (!positioningEl) return;

    const updatePosition = () => {
      const rect = positioningEl.getBoundingClientRect();
      const pos: CSSProperties = { top: rect.bottom + offset };

      if (placement === 'bottom-start') {
        pos.left = rect.left;
      } else {
        pos.right = window.innerWidth - rect.right;
      }

      setPosition(pos);
    };

    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, placement, offset, anchorEl, triggerRef]);

  if (!open || !mounted) return null;

  return createPortal(
    <div
      className={cx(styles.content, className)}
      id={contentId}
      ref={contentRef as React.RefObject<HTMLDivElement>}
      role="dialog"
      style={{ ...collisionStyle, ...position, ...zIndexStyle, ...style }}
    >
      {children}
    </div>,
    document.body,
  );
};

// Storybook docgen를 위해 래핑
export const PopOverContent = (props: PopOverContentProps) => {
  return <PopOverContentImpl {...props} />;
};

PopOverContent.displayName = 'PopOver.Content';
